import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useDataContext from 'data/context/data';

import useCouponVerification from '../hooks/useCouponVerification';
import useOrderPrice from '../hooks/useOrderPrice';
import { normalizePromoCode } from '../lib/orderPriceRequest';
import { quotePromo } from '../lib/couponVerdict';
import { couponCoversCart, utcDay } from '../lib/coupon';
import useCartContext from './cart';
import useUserContext from './user';

// Backend User::REFERRAL_CODE_LENGTH. A referral code rides the same promoCode field but is not a
// coupon: coupons/verify would reject it, so it is never verified. Coupon codes are 5 characters.
const REFERRAL_CODE_LENGTH = 6;

const UNKNOWN = { status: 'unknown', summary: null, message: null };

const EMPTY = {
  code: undefined,
  status: 'unknown',
  summary: null,
  message: null,
  previewSummary: null,
  coveredProductNames: [],
  coversCart: true,
  minimumMet: false,
  remove: () => undefined,
};

const ClaimedCouponContext = createContext(EMPTY);

// The ids the cart's price request sends: a bundle id stands for its nested products.
const cartProductIds = (items) =>
  [...new Set(items.map((item) => (item.bundle ? item.bundle.id : item.productId)))].sort();

// Milliseconds until the next midnight UTC, when the backend's `Date.today` moves on.
const msUntilNextUtcDay = (now) => {
  const next = new Date(`${utcDay(now)}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next - now;
};

const coveredProductNames = (products, productIds) => {
  const names = productIds.map(
    (id) =>
      products.find((product) =>
        [...product.options, ...product.bundles].some((member) => member.id === id),
      )?.name,
  );
  return [...new Set(names.filter(Boolean))];
};

/**
 * The cart's claimed coupon (`?coupon=` URL param, cart input, referral code) with its advisory
 * verdict from POST /api/v1/coupons/verify, so the catalog can describe the offer before the
 * cart is priced. Verification is keyed by code, account and UTC day. Cart contents are not
 * sent: a coupon restricted to photo books must still show on photo books while a print sits in
 * the cart. The restriction is applied locally per product (`couponAppliesTo`), and `coversCart`
 * says whether anything in the cart qualifies.
 *
 * A coupon with an order minimum is the one case where the cart decides what the store shows:
 * the catalog previews it only after a cart quote (POST /api/v1/orders/price with the code)
 * confirms the minimum is met, and shows nothing for it until then. The quote stays the
 * authority on the money.
 *
 * status: 'unknown' (nothing claimed, in flight, or a can't-check), 'valid', 'invalid', or
 * 'referral' (a referral code, never verified).
 */
export const ClaimedCouponProvider = (props) => {
  const cartContext = useCartContext();
  const userContext = useUserContext();
  const dataContext = useDataContext();

  // The backend's calendar day. A coupon expires at midnight UTC, so the verdict is re-verified
  // when that day rolls over while the tab stays open.
  const [day, setDay] = useState(() => utcDay());

  useEffect(() => {
    const timer = setTimeout(() => setDay(utcDay()), msUntilNextUtcDay(new Date()));
    return () => clearTimeout(timer);
  }, [day]);

  const code = cartContext.isLoading
    ? undefined
    : normalizePromoCode(cartContext.promoCode) || undefined;
  const isReferral = code?.length === REFERRAL_CODE_LENGTH;
  const productIdsKey = cartProductIds(cartContext.items).join(',');
  const { accessToken } = userContext;
  const verification = useCouponVerification(
    code,
    accessToken,
    day,
    Boolean(code) && !isReferral && !userContext.isLoading,
  );
  const verdict = verification.data ?? UNKNOWN;

  // A stored claim the backend now rejects (expired, inactive, usage limit reached) is dropped
  // quietly, so it cannot keep failing the order quote on every later visit.
  useEffect(() => {
    if (!code || isReferral || verdict.status !== 'invalid') return;
    cartContext.removePromoCode('verification');
  }, [code, isReferral, verdict.status]);

  const requiresMinimum = verdict.summary?.requiredMinimumValue > 0;
  const quote = useOrderPrice({
    accessToken,
    items: cartContext.items,
    countryCode: dataContext.countryCode,
    promoCode: code,
    enabled: requiresMinimum && !userContext.isLoading && !cartContext.isLoading,
    keepPrevious: true,
  });
  const minimumMet = requiresMinimum && quotePromo(quote.data, code)?.status === 'applied';

  const value = useMemo(() => {
    const { summary } = verdict;
    const cartIds = productIdsKey ? productIdsKey.split(',') : [];

    return {
      code,
      status: isReferral ? 'referral' : verdict.status,
      summary,
      message: verdict.message,
      // The catalog previews an unconditional coupon right away and a coupon with an order
      // minimum only once the cart quote confirmed the minimum.
      previewSummary: summary && (!requiresMinimum || minimumMet) ? summary : null,
      coveredProductNames: summary?.productIds
        ? coveredProductNames(dataContext.products, summary.productIds)
        : [],
      coversCart: couponCoversCart(summary, cartIds),
      minimumMet,
      remove: cartContext.removePromoCode,
    };
  }, [
    code,
    isReferral,
    verdict,
    requiresMinimum,
    minimumMet,
    productIdsKey,
    dataContext.products,
    cartContext.removePromoCode,
  ]);

  return (
    <ClaimedCouponContext.Provider value={value}>{props.children}</ClaimedCouponContext.Provider>
  );
};

ClaimedCouponProvider.propTypes = {
  children: PropTypes.node,
};

const useClaimedCoupon = () => useContext(ClaimedCouponContext);

export default useClaimedCoupon;
